import { CustomField, LAYOUT_COLUMNS, LayoutRow } from '@ee/common/models';

/**
 * Move what will not fit onto a row of its own.
 *
 * A row asking for more than twelve columns cannot be rendered — the grid has twelve — so the canvas was
 * showing a layout the form could never produce, and blocking Publish over it. Worse, it was not always
 * fixable by hand: a locked placement offers no resize, so a row of two twelves where one of them is
 * locked has nothing a firm is allowed to shrink.
 *
 * Order is preserved and nothing is dropped or resized; the overflow simply starts the next row, which
 * is what the greedy pack the migrator runs would have produced in the first place.
 */
export function repackOverfullRows(rows: LayoutRow[]): LayoutRow[] {
  if (!rows.some(row => row.items.reduce((total, item) => total + (item.span ?? 0), 0) > LAYOUT_COLUMNS)) {
    return rows;
  }

  const packed: LayoutRow[] = [];
  rows.forEach(row => {
    let current: LayoutRow = { ...row, items: [] };
    let used = 0;
    row.items.forEach(item => {
      const span = item.span ?? LAYOUT_COLUMNS;
      if (current.items.length && used + span > LAYOUT_COLUMNS) {
        packed.push(current);
        // Suffixed rather than reusing the id: two rows sharing one id breaks `track row.id` on the
        // canvas, which is how a drag ends up applying to the wrong row.
        current = { ...row, id: `${row.id}-${packed.length}`, items: [] };
        used = 0;
      }
      current.items.push(item);
      used += span;
    });
    packed.push(current);
  });
  return packed;
}

/** Keep the first field of each key. A field with no key is kept. */
export function dedupeByKey(fields: CustomField[]): CustomField[] {
  const seen = new Set<string>();
  return fields.filter(field => {
    const key = field.field;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
